import {createTheme} from '@mui/material/styles';

/**
 * MAKSMART CRM Theme
 * Water Quality & Filtration Theme
 *
 * Color Philosophy:
 * - Primary (Blue): Water, purity, trust, cleanliness
 * - Secondary (Teal/Cyan): Fresh water, technology, monitoring
 * - Success (Green): Clean water, healthy, safe
 * - Info (Light Blue): Information, data, analytics
 * - Warning (Amber): Warning levels, attention needed
 * - Error (Red): Contamination, critical issues
 */

const theme = createTheme({
    palette: {
        mode: 'light',

        // Primary - Indigo/Deep Blue
        primary: {
            light: '#5C6BC0',      // Light indigo
            main: '#3F51B5',       // Indigo - professional & modern
            dark: '#303F9F',       // Deep indigo
            contrastText: '#FFFFFF',
        },

        // Secondary - Blue
        secondary: {
            light: '#64B5F6',      // Light blue
            main: '#1976D2',       // Blue - corporate & trustworthy
            dark: '#1565C0',       // Dark blue
            contrastText: '#FFFFFF',
        },

        // Success - Clean/Healthy Green
        success: {
            light: '#81C784',      // Light green - safe water
            main: '#43A047',       // Green - healthy, clean
            dark: '#2E7D32',       // Dark green
            contrastText: '#FFFFFF',
        },

        // Info - Sky Blue (Analytics/Data)
        info: {
            light: '#4FC3F7',      // Light blue - information
            main: '#29B6F6',       // Bright blue - data
            dark: '#0277BD',       // Deep blue
            contrastText: '#FFFFFF',
        },

        // Warning - Amber (Attention)
        warning: {
            light: '#FFB74D',      // Light amber
            main: '#FB8C00',       // Amber - needs attention
            dark: '#E65100',       // Dark amber
            contrastText: '#000000',
        },

        // Error - Red (Critical)
        error: {
            light: '#E57373',      // Light red
            main: '#D32F2F',       // Red - critical issues
            dark: '#B71C1C',       // Dark red
            contrastText: '#FFFFFF',
        },

        // Background
        background: {
            default: '#F5F5F5',    // Light gray
            paper: '#FFFFFF',      // White
        },

        // Text
        text: {
            primary: '#212121',    // Almost black
            secondary: '#757575',  // Gray
            disabled: '#BDBDBD',   // Light gray
        },

        // Divider
        divider: '#E0E0E0',

        // Custom colors for water quality indicators
        waterQuality: {
            excellent: '#43A047',  // Green - excellent quality
            good: '#7CB342',       // Light green - good quality
            moderate: '#FDD835',   // Yellow - moderate quality
            poor: '#FB8C00',       // Amber - poor quality
            critical: '#D32F2F',   // Red - critical quality
        },

        // Device status colors
        deviceStatus: {
            online: '#43A047',     // Green - device online
            offline: '#757575',    // Gray - device offline
            warning: '#FB8C00',    // Amber - warning state
            error: '#D32F2F',      // Red - error state
        },

        // Gradient colors for backgrounds and effects
        gradients: {
            // Professional gradients
            primary: 'linear-gradient(135deg, #3F51B5 0%, #1976D2 100%)',
            dark: 'linear-gradient(135deg, #303F9F 0%, #1565C0 100%)',
            light: 'linear-gradient(135deg, #5C6BC0 0%, #64B5F6 100%)',
            accent: 'linear-gradient(135deg, #1976D2 0%, #43A047 100%)',

            // Status gradients
            success: 'linear-gradient(135deg, #43A047 0%, #7CB342 100%)',
            warning: 'linear-gradient(135deg, #FB8C00 0%, #FDD835 100%)',
            error: 'linear-gradient(135deg, #D32F2F 0%, #E64A19 100%)',
        },
    },

    // Typography
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),

        h1: {
            fontWeight: 700,
            fontSize: '2.5rem',
            lineHeight: 1.2,
        },
        h2: {
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: 1.3,
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.75rem',
            lineHeight: 1.3,
        },
        h4: {
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.4,
        },
        h5: {
            fontWeight: 600,
            fontSize: '1.25rem',
            lineHeight: 1.4,
        },
        h6: {
            fontWeight: 600,
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        subtitle1: {
            fontSize: '1rem',
            lineHeight: 1.75,
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '0.875rem',
            lineHeight: 1.57,
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.43,
        },
        button: {
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
        },
    },

    // Shape
    shape: {
        borderRadius: 0, // Sharp corners for clean look
    },

    // Shadows - softer, more modern
    shadows: [
        'none',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.08)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 12px 24px rgba(0, 0, 0, 0.12)',
        '0px 16px 32px rgba(0, 0, 0, 0.15)',
        '0px 20px 40px rgba(0, 0, 0, 0.18)',
        '0px 24px 48px rgba(0, 0, 0, 0.2)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.08)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 12px 24px rgba(0, 0, 0, 0.12)',
        '0px 16px 32px rgba(0, 0, 0, 0.15)',
        '0px 20px 40px rgba(0, 0, 0, 0.18)',
        '0px 24px 48px rgba(0, 0, 0, 0.2)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.08)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 12px 24px rgba(0, 0, 0, 0.12)',
        '0px 16px 32px rgba(0, 0, 0, 0.15)',
        '0px 20px 40px rgba(0, 0, 0, 0.18)',
        '0px 24px 48px rgba(0, 0, 0, 0.2)',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.08)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
    ],

    // Component overrides
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: '8px 16px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0px 4px 12px rgba(63, 81, 181, 0.3)',
                    },
                },
                contained: {
                    '&:hover': {
                        boxShadow: '0px 6px 16px rgba(63, 81, 181, 0.4)',
                    },
                },
            },
        },

        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                },
            },
        },

        MuiPaper: {
            styleOverrides: {
                elevation1: {
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                },
                elevation2: {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                },
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: {},
            },
        },

        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                },
            },
        },

        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        fontWeight: 600,
                        backgroundColor: '#F5F5F5',
                        padding: '12px 16px',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '8px 16px',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: '51px',
                },
            },
        },
    },
});

export default theme;
